/**
 * Xactimate code split/join helpers (PRP Gotcha #3).
 *
 * `ScopeResponseLineItem.xactimate_code` is ONE whitespace-delimited
 * string. For display the V2 grid shows CAT and SEL as separate cells:
 *   token[0] -> cat, token[1] -> sel, remaining tokens -> rest.
 * On commit the parts are re-joined. Both functions degrade gracefully
 * when fewer tokens (or empty/undefined input) are present.
 */

/** The decomposed parts of an Xactimate code string. */
export interface XactimateCodeParts {
  cat: string;
  sel: string;
  rest: string;
}

/**
 * Splits a whitespace-delimited Xactimate code into CAT / SEL / rest.
 *
 * @param code - The full code string (may be empty/undefined).
 * @returns `{ cat, sel, rest }` with empty strings when tokens are absent.
 */
export function splitXactimateCode(code: string | undefined | null): XactimateCodeParts {
  const tokens = (code ?? '').trim().split(/\s+/).filter(Boolean);
  return {
    cat: tokens[0] ?? '',
    sel: tokens[1] ?? '',
    rest: tokens.slice(2).join(' '),
  };
}

/**
 * Joins CAT / SEL / rest back into a single Xactimate code string.
 *
 * @param cat - The category token.
 * @param sel - The selector token.
 * @param rest - Any trailing activity/modifier tokens.
 * @returns The space-joined code, with empty parts dropped.
 */
export function joinXactimateCode(cat: string, sel: string, rest?: string): string {
  return [cat, sel, rest]
    .map((p) => (p ?? '').trim())
    .filter(Boolean)
    .join(' ');
}
